/**
 * The basemap style, assembled locally from @protomaps/basemaps.
 *
 * Note on the package name: the prompt named `protomaps-themes-base`. That
 * package is deprecated upstream — npm reports "migrated to
 * @protomaps/basemaps with a new major version". Same library, same layer
 * output, new name; installing the retired one would pin us to a dead 4.x.
 *
 * Everything the style references is served from our own origin:
 *   /basemap/prague.pmtiles   the extract (scripts/build-basemap.sh)
 *   /fonts/{fontstack}/{range}.pbf   glyphs (scripts/fetch-map-assets.sh)
 *   /sprites/light            sprite sheet
 * There is no CDN in this file, and the CSP would block one if there were.
 */
import { layers, namedFlavor } from '@protomaps/basemaps';
import type { StyleSpecification } from 'maplibre-gl';

/** Source id shared by every basemap layer. */
export const BASE_SOURCE = 'protomaps';

/** Prague, roughly Karlův most, and the extract's bounds. */
export const PRAGUE_CENTER: [number, number] = [14.4378, 50.0755];
export const PRAGUE_BOUNDS: [number, number, number, number] = [14.22, 49.94, 14.71, 50.18];

/**
 * MČ Praha 6 (Dejvice, Bubeneč, Střešovice, Břevnov, Veleslavín, Ruzyně,
 * Liboc, Sedlec, edge of Suchdol), used only as the map's *default camera*.
 * The basemap extract, the data layers and the coordinate validation stay
 * city-wide: PRAGUE_BOUNDS above still drives the map's `maxBounds`, so the
 * whole city stays pannable from this starting view. (PRAGUE_CENTER is no
 * longer read by anything now that the camera opens on a fitted bounds; it is
 * kept because a city-wide view is a plausible thing to want back.) These
 * numbers are a hand-eyeballed rectangle around the district on
 * a map, not the authoritative cadastral boundary (which is a non-rectangular
 * polygon) — good enough to frame a `fitBounds`, not to test point-in-Praha-6.
 */
export const PRAHA6_CENTER: [number, number] = [14.35, 50.098];
export const PRAHA6_BOUNDS: [number, number, number, number] = [14.29, 50.07, 14.41, 50.13];

const ATTRIBUTION =
  '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap contributors</a> · ' +
  '<a href="https://github.com/protomaps/basemaps" target="_blank" rel="noopener">Protomaps</a>';

export function buildBaseStyle(): StyleSpecification {
  return {
    version: 8,
    // Referenced with `url:`, never a hand-wired `tiles:` template — the
    // PMTiles header carries minzoom/maxzoom, and bypassing it makes MapLibre
    // assume maxzoom 22 and request empty tiles instead of overzooming.
    sources: {
      [BASE_SOURCE]: {
        type: 'vector',
        url: `pmtiles://${window.location.origin}/basemap/prague.pmtiles`,
        attribution: ATTRIBUTION,
      },
    },
    glyphs: '/fonts/{fontstack}/{range}.pbf',
    sprite: `${window.location.origin}/sprites/light`,
    layers: layers(BASE_SOURCE, namedFlavor('light'), { lang: 'cs' }),
  } satisfies StyleSpecification;
}
